import React, { Dispatch, memo, useCallback, useEffect, useMemo, useState } from 'react';

import Modal from '@components/common/Modal';
import UploadFile from '@components/ModalComponents/ImportMapDataContainer/ImportMapData/UploadFile';
import ImportOranConfigurationDataModelForm
	from '@components/routes/CmModule/ConfigurationDataModelManagement/AttachSchemaModel/ImportOranConfigurationDataModelForm';

import { useCreateMavenirConfig } from '@hooks/configurationDataModelManagement/useCreateMavenirConfig';
import { useMutateAnalysisSchemaFiles } from '@hooks/configurationDataModelManagement/useMutateAnalysisSchemaFiles';
import { useMutateSchemaUpload } from '@hooks/configurationDataModelManagement/useMutateSchemaUpload';

import { isEmpty } from '@utils/common';
import { getVisibilityStyle } from '@utils/styleUtils';

import {
	EConfigurationDataFieldNames,
	EOranConfigurationDataFieldNames,
	IConfigurationDataAnalysisValues,
	IConfigurationDataFormValues,
	IOranConfigurationDataFormValues,
} from '@interfaces/cmModule/configurationData';
import { IAnalysisSchemaUrl } from '@interfaces/cmModule/schemaConfig';

import { EImportingDataType } from '@enums/dropzone';

import { MODAL_WIDTH_480 } from '@constants/modals';
import {
	acceptedFileTypesSchema,
	fileErrorsSchema,
} from '@components/routes/CmModule/ConfigurationDataModelManagement/constants';

import ImportConfigurationDataModelForm from '../ImportConfigurationDataModelForm';
import ReplaceConfirm from '../ReplaceConfirm';

interface IAttachSchemaConfig {
	onClose: () => void;
	isVisibleForm: boolean;
	setIsVisibleForm: Dispatch<React.SetStateAction<boolean>>;
	setIsConfirmModalNeeded: Dispatch<React.SetStateAction<boolean>>;
	isOranVendor: boolean;
}

const AttachSchemaConfig = ({
	onClose,
	isVisibleForm,
	setIsVisibleForm,
	setIsConfirmModalNeeded,
	isOranVendor,
}: IAttachSchemaConfig) => {
	const [formValues, setFormValues] = useState<Partial<IConfigurationDataAnalysisValues>>();
	const [fileNames, setFileNames] = useState<string[]>([]);
	const [importIds, setImportIds] = useState<string[]>([]);
	const [analyzedConfigs, setAnalyzedConfigs] = useState<IAnalysisSchemaUrl[]>([]);
	const [isConfirmModals, setIsConfirmModals] = useState<boolean>(false);
	const [isDirtyForm, setIsDirtyForm] = useState<boolean>(false);
	const [errorAnalysisSchema, setErrorAnalysisSchema] = useState<string | undefined>();

	const existingFiles = useMemo(() => {
		return analyzedConfigs
			?.flatMap((config) => config.items)
			.filter((item) => !item?.isFileAnalyzed);
	}, [analyzedConfigs]);

	const {
		getAnalysisSchema,
		isLoadingAnalysisSchema,
	} = useMutateAnalysisSchemaFiles({
		setAnalyzedConfigs,
		setIsConfirmModals,
		setErrorAnalysisSchema,
		isOranVendor,
	});

	const { createUploadUrlAttachment, isUploadingFile } = useMutateSchemaUpload({
		setImportIds,
		setAnalyzedConfigs,
		setIsConfirmModals,
		formValues,
		setErrorAnalysisSchema,
		isOranVendor,
	});

	const {
		createMavenirConfig,
		isLoadingCreateMavenirConfig,
	} = useCreateMavenirConfig({
		onClose,
		isOranVendor,
	});

	const onProceedClickHandler = useCallback(() => {
		if (!importIds?.length) return;

		if (existingFiles?.length) {
			setIsConfirmModals(true);

			return;
		} else if (analyzedConfigs.length) {
			setIsConfirmModalNeeded(false);

			createMavenirConfig({
				importId: importIds[0],
				isReplaceAllDuplicates: analyzedConfigs[0].isReplaceAllDuplicates,
				isSkipAllDuplicates: analyzedConfigs[0].isSkipAllDuplicates,
				...(!(analyzedConfigs[0].isReplaceAllDuplicates || analyzedConfigs[0].isSkipAllDuplicates)
						? {
							duplicateDecisionResults: analyzedConfigs[0].items.map((item) => ({
								id: item.id,
								isReplace: !!item.isReplace,
							})),
						}
						: {}
				),
			});
		}
	}, [analyzedConfigs, createMavenirConfig, existingFiles?.length, importIds, setIsConfirmModalNeeded]);

	const toggleForm = useCallback(() => {
		setIsVisibleForm(prevState => !prevState);
	}, [setIsVisibleForm]);

	const onSubmitForm = useCallback((values: IConfigurationDataFormValues) => {
		const formattedFormValues = {
			[EConfigurationDataFieldNames.NAME]: values[EConfigurationDataFieldNames.NAME]?.value || '',
			[EConfigurationDataFieldNames.DESCRIPTOR_TYPE]: values[EConfigurationDataFieldNames.DESCRIPTOR_TYPE]?.value || '',
			[EConfigurationDataFieldNames.DATA_MODEL_TYPE]: values[EConfigurationDataFieldNames.DATA_MODEL_TYPE]?.value || '',
			[EConfigurationDataFieldNames.DEVICE_TYPE]: values[EConfigurationDataFieldNames.DEVICE_TYPE]?.value || '',
			[EConfigurationDataFieldNames.VDU_ID]: Number(values[EConfigurationDataFieldNames.VDU_ID]?.value),
			[EConfigurationDataFieldNames.DATA_MODEL]: values[EConfigurationDataFieldNames.DATA_MODEL],
			[EConfigurationDataFieldNames.PNF_TYPE]: values[EConfigurationDataFieldNames.PNF_TYPE]?.value,
			[EConfigurationDataFieldNames.BUILD_VERSION]: values[EConfigurationDataFieldNames.BUILD_VERSION]?.value,
			[EConfigurationDataFieldNames.ELEMENT_TYPE]: values[EConfigurationDataFieldNames.ELEMENT_TYPE]?.value,
			[EConfigurationDataFieldNames.GROUP_BY]: values[EConfigurationDataFieldNames.GROUP_BY]?.value,
			[EConfigurationDataFieldNames.PRODUCT_NAME]: values[EConfigurationDataFieldNames.PRODUCT_NAME]?.value || '',
			[EConfigurationDataFieldNames.NS_ID]: Number(values[EConfigurationDataFieldNames.NS_ID]?.value || ''),
			[EConfigurationDataFieldNames.NS_VERSION]: values[EConfigurationDataFieldNames.NS_VERSION]?.value || '',
			[EConfigurationDataFieldNames.NF_TYPE]: Number(values[EConfigurationDataFieldNames.NF_TYPE]?.value),
			[EConfigurationDataFieldNames.COMPONENT_TYPE]:
				Number(values[EConfigurationDataFieldNames.COMPONENT_TYPE]?.value),
			[EConfigurationDataFieldNames.COMPONENT_VERSION]:
			values[EConfigurationDataFieldNames.COMPONENT_VERSION]?.value,
		};

		const preparedFormValues: Partial<IConfigurationDataAnalysisValues> = Object.entries(formattedFormValues)
			.filter(([_key, value]) => !!value)
			.reduce((obj, [key, value]) =>
				({ ...obj, [key]: value }), {});

		setFormValues(formattedFormValues);
		toggleForm();

		if (analyzedConfigs.length && importIds?.length && isDirtyForm && !isEmpty(preparedFormValues)) {
			getAnalysisSchema({
				...preparedFormValues,
				importId: importIds[0],
			});
		}
	}, [analyzedConfigs, getAnalysisSchema, importIds, isDirtyForm, toggleForm]);

	const onSubmitOranForm = useCallback((values: IOranConfigurationDataFormValues) => {
		const preparedValue: Partial<IConfigurationDataAnalysisValues> = {
			[EOranConfigurationDataFieldNames.NAME]: values[EConfigurationDataFieldNames.NAME]?.value,
		};

		setFormValues(preparedValue);
		toggleForm();

		if (analyzedConfigs.length && importIds?.length && isDirtyForm && !isEmpty(preparedValue)) {
			getAnalysisSchema({
				...preparedValue,
				importId: importIds[0],
			});
		}
	}, [analyzedConfigs, getAnalysisSchema, importIds, isDirtyForm, toggleForm]);

	const onCloseReplaceConfirms = useCallback(() => {
		setIsConfirmModals(false);
	}, []);

	const isLoadingUploadFile = isUploadingFile || isLoadingCreateMavenirConfig || isLoadingAnalysisSchema;

	useEffect(() => {
		// Set first default value when component is mounted
		setIsConfirmModalNeeded(isDirtyForm);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div>
			<Modal
				isOpen={!!existingFiles?.length && isConfirmModals}
				onClose={onCloseReplaceConfirms}
				width={MODAL_WIDTH_480}
			>
				<ReplaceConfirm
					existingFiles={existingFiles}
					isSingleFile={existingFiles.length === 1}
					setAnalyzedConfigs={setAnalyzedConfigs}
				/>
			</Modal>
			<div style={getVisibilityStyle(isVisibleForm)}>
				{isOranVendor
					? <ImportOranConfigurationDataModelForm
						onClose={onClose}
						onSubmitHandler={onSubmitOranForm}
						setIsConfirmModalNeeded={setIsConfirmModalNeeded}
						setIsDirty={setIsDirtyForm}
					/>
					: <ImportConfigurationDataModelForm
						onClose={onClose}
						onSubmitHandler={onSubmitForm}
						setIsConfirmModalNeeded={setIsConfirmModalNeeded}
						setIsDirty={setIsDirtyForm}
					/>
				}
			</div>
			<div style={getVisibilityStyle(!isVisibleForm)}>
				<UploadFile
					acceptedFileTypes={acceptedFileTypesSchema}
					chooseFileDescription='or drag here one ZIP archive with the YANG files'
					chooseFileText='Choose'
					closeButtonText='Back'
					createUploadUrl={createUploadUrlAttachment}
					errorMessage={errorAnalysisSchema}
					errors={fileErrorsSchema}
					fileNames={fileNames}
					importIds={importIds}
					importType={EImportingDataType.polygon}
					isDisabledNext={!!errorAnalysisSchema}
					isUploadingFile={isLoadingUploadFile}
					nextButtonText='Proceed'
					onClose={toggleForm}
					setCurrentComponent={onProceedClickHandler}
					setFileNames={setFileNames}
					setImportIds={setImportIds}
				/>
			</div>
		</div>
	);
};

export default memo(AttachSchemaConfig);
